import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container, Table, Button, Form, Card } from "react-bootstrap";
import axios from "axios";
import { baseURL } from "../../API/API";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import Header from "../../Components/common/Header";
import Footer from "../../Components/common/Footer";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCoins, faCopy, faMobileAlt, faUpload } from "@fortawesome/free-solid-svg-icons";

export default function ComboInvoice() {
  const { comboId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    payment_number: "01031360031",
    admin_instapay: "",
    support_phone: "01031360031",
    instructions: "",
  });

  const [paymentMethod, setPaymentMethod] = useState("Vodafone Cash");
  const [screenshot, setScreenshot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch settings & combo details
  useEffect(() => {
    async function fetchData() {
      try {
        const settingsRes = await axios.get(`${baseURL}/learning/settings`);
        if (settingsRes.data?.data) {
          setSettings(settingsRes.data.data);
        }
        
        const comboRes = await axios.get(`${baseURL}/learning/combos/detail/${comboId}`);
        setCombo(comboRes.data?.data);
      } catch (err) {
        console.error(err);
        toast.error("حدث خطأ أثناء تحميل الفاتورة");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [comboId]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="text-center py-5 text-muted">
          <h3>جاري تحميل الفاتورة...</h3>
        </div>
        <Footer />
      </>
    );
  }

  if (!combo) {
    return (
      <>
        <Header />
        <div className="text-center py-5 text-muted">
          <h3>الباقة غير موجودة</h3>
        </div>
        <Footer />
      </>
    );
  }

  const totalPrice = Number(combo.price);
  const discountVal = Number(combo.discount);
  const finalPrice = Math.max(totalPrice - discountVal, 0);

  // Select active payment detail based on combo configurations
  const activeVodafoneCash = combo.vodafone_cash || settings.payment_number;
  const activeInstaPay = combo.instapay || settings.admin_instapay;

  const handleCopyText = (text, typeName) => {
    navigator.clipboard.writeText(text);
    toast.success(`تم نسخ ${typeName} بنجاح!`);
  };

  const handleFileChange = (e) => {
    setScreenshot(e.target.files[0]);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!screenshot) {
      toast.warning("يرجى رفع لقطة شاشة (إثبات التحويل) لإتمام العملية");
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("amount", finalPrice);
    formData.append("method", paymentMethod);
    formData.append("screenshot", screenshot);
    formData.append("combo_id", combo.id);

    try {
      await axiosPrivate.post(`${baseURL}/payment/submit-credit-request`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("تم إرسال طلب الدفع بنجاح. سيتم مراجعة طلبك وتفعيل الباقة خلال ساعات!");
      navigate("/");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "فشل إرسال طلب الدفع، يرجى المحاولة لاحقاً.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build instruction steps list
  const instructionList = settings.instructions
    ? settings.instructions.split("\n").filter(step => step.trim() !== "")
    : [
        "افتح تطبيق محفظتك الإلكترونية أو تطبيق انستا باي (InstaPay).",
        `اختر تحويل أموال وأدخل الرقم الموحد: ${paymentMethod === "Vodafone Cash" ? activeVodafoneCash : activeInstaPay}`,
        `أدخل القيمة المطلوبة للتحويل وهي: ${finalPrice.toFixed(0)} جنيه.`,
        "أكد عملية التحويل، والتقط لقطة شاشة (Screenshot) واضحة تؤكد نجاح التحويل.",
        'اضغط على زر "رفع إثبات التحويل" بالأسفل وارفع الصورة، ثم اضغط على "ارسل طلب الدفع".'
      ];

  return (
    <div className="ComboInvoicePage" dir="rtl" style={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <Header />

      <Container className="my-5" style={{ maxWidth: "800px" }}>
        <Link to={`/combos/${combo.id}`} className="text-decoration-none d-inline-flex align-items-center gap-2 mb-4 text-secondary">
          <FontAwesomeIcon icon={faArrowRight} />
          <span>العودة للباقة</span>
        </Link>

        {/* Invoice Card */}
        <Card className="border-0 shadow rounded-4 p-4 text-center bg-white mb-4">
          <h2 className="fw-bold mb-4 text-dark position-relative d-inline-block mx-auto pb-2" style={{ borderBottom: "3px solid var(--brand-300)" }}>
            فاتورة الاشتراك بالباقة
          </h2>

          <div className="table-responsive mb-4">
            <Table bordered className="align-middle text-center m-0">
              <thead style={{ backgroundColor: "#f3f4f6" }}>
                <tr>
                  <th>اسم الباقة</th>
                  <th>الخصم</th>
                  <th>الإجمالي النهائي</th>
                </tr>
              </thead>
              <tbody>
                <tr className="fs-5">
                  <td className="fw-bold">{combo.name}</td>
                  <td className="text-danger font-bold">{discountVal.toFixed(0)} ج.م</td>
                  <td className="text-primary fw-bold fs-4">{finalPrice.toFixed(0)} ج.م</td>
                </tr>
              </tbody>
            </Table>
          </div>

          <div className="bg-light p-4 rounded-4 text-end">
            <h4 className="fw-bold mb-4 text-dark">تفاصيل وبيانات الدفع:</h4>
            
            {/* Payment Method Switcher */}
            <div className="d-flex justify-content-center gap-3 mb-4">
              <Button 
                variant={paymentMethod === "Vodafone Cash" ? "danger" : "outline-danger"}
                onClick={() => setPaymentMethod("Vodafone Cash")}
                className="px-4 py-2 rounded-pill fw-bold"
                disabled={!activeVodafoneCash}
              >
                فودافون كاش ({activeVodafoneCash})
              </Button>
              <Button 
                variant={paymentMethod === "InstaPay" ? "success" : "outline-success"}
                onClick={() => setPaymentMethod("InstaPay")}
                className="px-4 py-2 rounded-pill fw-bold"
                disabled={!activeInstaPay}
              >
                انستا باي ({activeInstaPay ? "متاح" : "غير متوفر"})
              </Button>
            </div>

            <div className="border rounded-3 p-3 text-center bg-white mb-4">
              <p className="mb-2 text-muted">
                يرجى تحويل مبلغ وقدره <strong className="fs-5 text-dark">{finalPrice.toFixed(0)} جنيه مصري</strong> عبر {paymentMethod === "Vodafone Cash" ? "محفظة فودافون كاش" : "تطبيق انستا باي"} إلى العنوان التالي:
              </p>
              
              <div className="d-flex align-items-center justify-content-center gap-3">
                <span className="fs-3 fw-bold text-dark font-bold letter-spacing-1">
                  {paymentMethod === "Vodafone Cash" ? activeVodafoneCash : activeInstaPay}
                </span>
                <Button 
                  size="sm" 
                  variant="outline-secondary" 
                  onClick={() => handleCopyText(paymentMethod === "Vodafone Cash" ? activeVodafoneCash : activeInstaPay, paymentMethod === "Vodafone Cash" ? "رقم التحويل" : "عنوان انستا باي")}
                  className="flex-center-both gap-2 px-3 py-1 rounded"
                >
                  <FontAwesomeIcon icon={faCopy} />
                  <span>نسخ</span>
                </Button>
              </div>
            </div>

            <h5 className="fw-bold text-dark mb-3">خطوات إتمام عملية الاشتراك:</h5>
            <ol className="p-0 pr-4 m-0" style={{ listStyleType: "decimal", lineHeight: "1.8", fontSize: "0.95rem" }}>
              {instructionList.map((step, idx) => (
                <li key={idx} className="mb-2 text-muted">{step}</li>
              ))}
            </ol>
          </div>
        </Card>

        {/* Upload form */}
        <Card className="border-0 shadow rounded-4 p-4 bg-white">
          <Form onSubmit={handleSubmitPayment} className="text-end">
            <h4 className="fw-bold text-dark mb-4">رفع إثبات التحويل</h4>
            
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold text-muted mb-2">لقطة شاشة التحويل (Screenshot):</Form.Label>
              <div 
                className="border-2 border-dashed rounded-3 p-4 text-center cursor-pointer bg-light"
                style={{ border: "2px dashed #cbd5e1" }}
              >
                <input 
                  type="file" 
                  accept="image/*" 
                  id="screenshot-file" 
                  onChange={handleFileChange}
                  className="d-none"
                  required
                />
                <label htmlFor="screenshot-file" className="cursor-pointer w-100 m-0">
                  <FontAwesomeIcon icon={faUpload} className="text-primary fs-2 mb-3" />
                  <p className="fw-bold text-dark mb-1">اسحب الصورة هنا أو اضغط للاختيار</p>
                  <p className="small text-muted m-0">يُقبل امتدادات صور jpeg, png, jpg وبحجم أقصى 2 ميجابايت</p>
                  {screenshot && (
                    <div className="mt-3 text-success fw-bold">
                      تم تحديد ملف: {screenshot.name}
                    </div>
                  )}
                </label>
              </div>
            </Form.Group>

            <Button 
              type="submit" 
              disabled={isSubmitting || !screenshot}
              className="w-100 rounded-pill py-3 fw-bold fs-5 flex-center-both gap-2"
              style={{
                backgroundColor: "var(--brand-300)",
                borderColor: "var(--brand-300)",
                color: "#fff"
              }}
            >
              <FontAwesomeIcon icon={faCoins} />
              <span>{isSubmitting ? "جاري إرسال الطلب..." : "ارسل طلب إثبات الدفع والتحويل"}</span>
            </Button>
          </Form>
        </Card>
      </Container>

      <Footer />
    </div>
  );
}
